import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Lock, X } from 'lucide-react'
import { usePersonalBankroll } from '@/hooks/usePersonalBankroll'
import { playTicketErrorMessage, usePlayTicket } from '@/hooks/useQuiniela'
import { cn, formatKickoff } from '@/lib/utils'
import { formatEur, kindOf, kindsSummary } from '@/lib/quiniela'
import type { GoalCat, MyTicketRef, PlayTicketPayload, QuinielaCurrent, QuinielaTicket, Sign } from '@/types/quiniela'
import { KIND_STYLE } from './MatchProbRow'

export interface TicketSummaryData {
  columns: number
  cost: number
  dobles: number
  triples: number
  selections: { position: number; signs: Sign[] }[]
  pleno: { home: GoalCat; away: GoalCat } | null
}

/** Mismo patrón visual que `PlayedBadge` (LA JUGUÉ de apuestas normales), adaptado a La Quiniela: sin cuota. */
export function QuinielaPlayedBadge({ columns, cost }: { columns: number; cost: number }) {
  return (
    <Link
      to="/mas/historial?tab=apuestas&sub=boletos"
      className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-brand-soft/20 border border-brand/30 text-brand text-[10px] font-black uppercase tracking-wider"
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      JUGADA · {columns} apuestas · {formatEur(cost)}
    </Link>
  )
}

function BoletoGrid({ selections, pleno }: Pick<TicketSummaryData, 'selections' | 'pleno'>) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 gap-1">
        {selections.map((s) => {
          const kind = kindOf(s.signs.length) ?? 'simple'
          return (
            <div key={s.position} className={cn('rounded-lg border px-0.5 py-1 text-center', KIND_STYLE[kind].chip)}>
              <p className="text-[8px] font-bold opacity-70 leading-none">{s.position}</p>
              <p className="figure text-[11px] font-black leading-tight mt-0.5">{s.signs.join('')}</p>
            </div>
          )
        })}
      </div>
      {pleno && (
        <div className="flex items-center justify-between rounded-lg border border-gold/40 bg-gold/10 px-2.5 py-1.5">
          <span className="text-[10px] font-black uppercase tracking-wider text-gold">Pleno al 15</span>
          <span className="figure text-sm font-black text-gold">{pleno.home} – {pleno.away}</span>
        </div>
      )}
    </div>
  )
}

/** Confirmación sencilla de LA JUGUÉ: jornada, apuestas, coste y resumen del boleto. Sin cuota ni stake manual. */
function PlayTicketModal({
  current, summary, onConfirm, pending, error, done, onClose,
}: {
  current: QuinielaCurrent
  summary: TicketSummaryData
  onConfirm: () => void
  pending: boolean
  error: string | null
  done: QuinielaTicket | null
  onClose: () => void
}) {
  const { data: bankroll } = usePersonalBankroll()
  const hasBankroll = !!bankroll?.configured
  const number = current.jornada?.number

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-sm p-5 space-y-4 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="LA JUGUÉ">
        {done ? (
          <div className="text-center space-y-3 py-2">
            <CheckCircle2 className="h-10 w-10 mx-auto text-brand" />
            <p className="text-sm font-black text-text">¡Boleto registrado!</p>
            <p className="text-xs text-text-dim leading-relaxed">
              Jornada {number} · {done.columns} apuestas · {formatEur(done.cost_eur)}.
              {done.stake_deducted ? ' Se ha descontado de tu Mi Banca.' : ''} Puedes seguirlo en Historial → Mis apuestas → Mis boletos.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={onClose} className="btn-secondary-app !py-2.5">Cerrar</button>
              <Link to="/mas/historial?tab=apuestas&sub=boletos" onClick={onClose} className="btn-brand-app !py-2.5">Ver Mis boletos</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm font-black text-text">LA JUGUÉ</p>
              <button onClick={onClose} className="p-1 text-text-dim hover:text-text" aria-label="Cerrar"><X className="h-4 w-4" /></button>
            </div>

            <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40 space-y-2.5">
              <div className="flex items-baseline justify-between">
                <p className="text-xs font-bold text-text">La Quiniela · Jornada {number}</p>
                <p className="text-[10px] text-text-dim">{kindsSummary(summary.dobles, summary.triples)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-semibold text-text-dim">Apuestas</p>
                  <p className="figure text-2xl font-black text-text leading-none mt-1">{summary.columns}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-text-dim">Coste</p>
                  <p className="figure text-2xl font-black text-brand leading-none mt-1">{formatEur(summary.cost)}</p>
                </div>
              </div>
            </div>

            <BoletoGrid selections={summary.selections} pleno={summary.pleno} />

            <p className="text-[11px] text-text-muted leading-snug">
              {hasBankroll
                ? <>Se descontarán <span className="font-bold text-text">{formatEur(summary.cost)}</span> de tu Mi Banca (saldo {formatEur(bankroll?.current_balance ?? 0)}).</>
                : 'No tienes Mi Banca configurada: el boleto se registra igualmente, sin mover ningún saldo.'}
              {current.sales_close_at && <> Ventas abiertas hasta <span className="figure text-text">{formatKickoff(current.sales_close_at)}</span>.</>}
            </p>

            {error && <p className="text-[11px] text-danger" role="alert">{error}</p>}

            <div className="grid grid-cols-2 gap-2">
              <button onClick={onClose} className="btn-secondary-app !py-2.5">Cancelar</button>
              <button onClick={onConfirm} disabled={pending} className="btn-brand-app !py-2.5 disabled:opacity-50">
                {pending ? 'Registrando…' : 'Confirmar jugada'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** LA JUGUÉ de La Quiniela. Muestra el botón (o JUGADA si ya está registrado ese boleto) y el modal de confirmación.
 * `payload` solo dice QUÉ boleto es; el servidor reconstruye coste, columnas y probabilidades. */
export function PlayTicketAction({
  current, payload, summary, played, blockedReason, onPlayed,
}: {
  current: QuinielaCurrent
  payload: PlayTicketPayload | null
  summary: TicketSummaryData | null
  /** Boleto ya registrado que coincide con este (propuesta de JA ya jugada, o el que acaba de registrar el usuario). */
  played?: Pick<MyTicketRef, 'columns' | 'cost_eur'> | null
  blockedReason?: string | null
  onPlayed?: (t: QuinielaTicket) => void
}) {
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState<QuinielaTicket | null>(null)
  const [error, setError] = useState<string | null>(null)
  const play = usePlayTicket(current.jornada?.id)
  const closed = current.sales_open === false

  // Con el modal abierto (p. ej. mostrando «¡Boleto registrado!») no se sustituye por el badge hasta cerrarlo.
  if (played && !open) return <QuinielaPlayedBadge columns={played.columns} cost={played.cost_eur} />

  const disabledReason = closed ? 'Ventas cerradas' : blockedReason ?? (!payload || !summary ? 'Completa tu boleto' : null)

  const confirm = () => {
    if (!payload) return
    setError(null)
    play.mutate(payload, {
      onSuccess: (data) => { setDone(data.ticket); onPlayed?.(data.ticket) },
      onError: (e) => setError(playTicketErrorMessage(e)),
    })
  }
  const close = () => { setOpen(false); setError(null); setDone(null) }

  return (
    <>
      {!played && (
        <button
          type="button" disabled={!!disabledReason} onClick={() => setOpen(true)}
          className="btn-brand-app w-full !py-3 disabled:opacity-45 disabled:cursor-not-allowed"
        >
          {closed ? <Lock className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {disabledReason ? `LA JUGUÉ · ${disabledReason}` : 'LA JUGUÉ'}
        </button>
      )}
      {open && summary && (
        <PlayTicketModal
          current={current} summary={summary} onConfirm={confirm} pending={play.isPending}
          error={error} done={done} onClose={close}
        />
      )}
    </>
  )
}
